import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { ChevronDown, LogOut, X } from "lucide-react";
import { NAV_GROUPS } from "./navConfig";
import { isMasterDataAdmin } from "../../Services/warehouseContext";
import curebayLogo from "../../Assets/CurebayLogo.svg";

/* Decide whether a sub-item is the active route, allowing for the
   report/profile families that fan out across several paths. */
const childIsActive = (childPath, pathname) => {
  if (pathname === childPath) return true;
  if (childPath === "/consignment/che") return pathname.startsWith("/consignment");
  if (childPath === "/orderReport") return /orderreport/i.test(pathname);
  if (childPath === "/b2bKycReport") return pathname.startsWith("/b2bProfile");
  return false;
};

/* Entries flagged adminOnly (today: the medicine-request approval queue) are
   dropped for non-admins — sub-items and whole top-level items alike, so the flag
   means the same thing wherever it is put. Hiding the link is not the access
   control — the route is guarded too (RequireAdmin in Route/routing.jsx). */
const visibleChildren = (item) =>
  (item.children || []).filter((c) => !c.adminOnly || isMasterDataAdmin());
const itemVisible = (item) => !item.adminOnly || isMasterDataAdmin();

const SideNav = ({ open: drawerOpen = false, onClose }) => {
  const navigate = useNavigate();
  const { pathname } = useLocation();

  /* mobile drawer closes itself once a route is picked */
  useEffect(() => {
    onClose?.();
  }, [pathname]);

  let user = {};
  try {
    user = JSON.parse(localStorage.getItem("loginObj")) || {};
  } catch (e) {
    user = {};
  }
  const fullName = [user.firstName, user.lastName].filter(Boolean).join(" ") || "Pharmacy user";
  const initials =
    [user.firstName, user.lastName]
      .filter(Boolean)
      .map((n) => n[0])
      .join("")
      .toUpperCase() || "CB";

  /* a group/parent starts expanded when it owns the active route */
  const ownsActive = (item) =>
    visibleChildren(item).some((c) => childIsActive(c.path, pathname));
  const [open, setOpen] = useState(() => {
    const init = {};
    NAV_GROUPS.forEach((g) =>
      g.items.forEach((it) => {
        if (it.children) init[it.id] = ownsActive(it) || it.id === "orders";
      })
    );
    return init;
  });

  const handleLogout = () => {
    localStorage.clear();
    navigate("/");
  };

  const renderItem = (item) => {
    if (!itemVisible(item)) return null;
    const Icon = item.icon;
    const children = visibleChildren(item);
    const hasChildren = !!item.children;
    const expanded = !!open[item.id];

    /* A parent whose only entries were admin-only disappears entirely rather
       than falling through to the leaf branch (it has no path of its own). */
    if (hasChildren && children.length === 0) return null;

    if (!hasChildren) {
      const active = item.path === pathname;
      return (
        <button
          key={item.id}
          onClick={() => navigate(item.path)}
          className={[
            "group flex items-center gap-2.5 w-full text-left rounded-cb-sm",
            "px-3 py-[7px] text-t-md transition-colors duration-150",
            active
              ? "bg-surface-selected text-ink-700 font-semibold shadow-[inset_2px_0_0_var(--ink-600)]"
              : "text-fg-default font-medium hover:bg-surface-hover",
          ].join(" ")}
        >
          <Icon
            size={17}
            strokeWidth={1.6}
            className={active ? "text-ink-600" : "text-fg-muted"}
          />
          <span className="flex-1">{item.label}</span>
        </button>
      );
    }

    const groupActive = ownsActive(item);
    return (
      <div key={item.id}>
        <button
          /* Accordion + jump-to-first: opening a section collapses the others
             (replace the map instead of spreading) and renders its first child.
             Re-clicking an open section just collapses it (no navigation). */
          onClick={() => {
            const isOpen = !!open[item.id];
            if (!isOpen && children[0]?.path) navigate(children[0].path);
            setOpen(isOpen ? {} : { [item.id]: true });
          }}
          className={[
            "flex items-center gap-2.5 w-full text-left rounded-cb-sm",
            "px-3 py-[7px] text-t-md transition-colors duration-150",
            groupActive
              ? "text-ink-700 font-semibold"
              : "text-fg-default font-medium hover:bg-surface-hover",
          ].join(" ")}
        >
          <Icon
            size={17}
            strokeWidth={1.6}
            className={groupActive ? "text-ink-600" : "text-fg-muted"}
          />
          <span className="flex-1">{item.label}</span>
          <ChevronDown
            size={13}
            strokeWidth={1.9}
            className={[
              "text-fg-muted transition-transform duration-150",
              expanded ? "rotate-0" : "-rotate-90",
            ].join(" ")}
          />
        </button>
        {expanded && (
          <div className="flex flex-col gap-px pl-[34px] py-0.5 mb-0.5">
            {children.map((c) => {
              const active = childIsActive(c.path, pathname);
              return (
                <button
                  key={c.path}
                  onClick={() => navigate(c.path)}
                  className={[
                    "flex items-center w-full text-left rounded-cb-sm",
                    "px-2.5 py-1.5 text-t-sm transition-colors duration-150",
                    active
                      ? "bg-surface-selected text-ink-700 font-semibold shadow-[inset_2px_0_0_var(--ink-600)]"
                      : "text-fg-muted font-normal hover:bg-surface-hover hover:text-fg-default",
                  ].join(" ")}
                >
                  {c.label}
                </button>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  return (
    <>
    {/* backdrop for the mobile drawer */}
    {drawerOpen && (
      <div
        className="fixed inset-0 z-[55] lg:hidden cb-fade-in"
        style={{ background: "var(--bg-overlay)" }}
        onClick={onClose}
      />
    )}
    <aside
      className={[
        "shrink-0 h-[100dvh] bg-panel border-r border-hairline flex flex-col",
        "fixed inset-y-0 left-0 z-[56] max-w-[85vw] transition-transform duration-200",
        "lg:static lg:z-auto lg:max-w-none lg:translate-x-0",
        drawerOpen ? "translate-x-0 shadow-cb-lg" : "-translate-x-full",
      ].join(" ")}
      style={{ width: "var(--sidebar-w)" }}
    >
      {/* brand */}
      <div className="px-4 h-[52px] flex items-center justify-between border-b border-hairline-subtle shrink-0">
        <img src={curebayLogo} alt="CureBay" className="h-7" />
        <button
          onClick={onClose}
          aria-label="Close menu"
          className="lg:hidden w-8 h-8 flex items-center justify-center rounded-cb-sm text-fg-muted hover:bg-surface-hover"
        >
          <X size={18} strokeWidth={1.75} />
        </button>
      </div>

      {/* nav */}
      <nav className="flex-1 overflow-y-auto cb-scroll px-2 py-3 flex flex-col gap-0.5">
        {NAV_GROUPS.map((g) => (
          <div key={g.group} className="flex flex-col gap-0.5">
            <div className="flex items-center gap-2 px-3 pt-3 pb-1">
              <span className="text-[9px] font-semibold uppercase tracking-[0.08em] text-fg-subtle">
                {g.group}
              </span>
              <span className="flex-1 h-px bg-hairline-subtle" />
            </div>
            {g.items.map(renderItem)}
          </div>
        ))}
      </nav>

      {/* user */}
      <div className="px-2.5 py-2.5 border-t border-hairline-subtle shrink-0">
        <div className="flex items-center gap-2.5 px-1.5">
          <div className="w-7 h-7 rounded-full bg-ink-600 text-white flex items-center justify-center text-[11px] font-semibold shrink-0">
            {initials}
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-t-sm font-semibold text-fg-strong leading-tight truncate">
              {fullName}
            </div>
            <div className="text-[10px] text-fg-muted font-mono truncate">
              admin · curebay.in
            </div>
          </div>
          <button
            onClick={handleLogout}
            title="Log out"
            className="w-7 h-7 flex items-center justify-center rounded-cb-sm text-fg-muted hover:bg-red-50 hover:text-red-700 transition-colors"
          >
            <LogOut size={15} strokeWidth={1.6} />
          </button>
        </div>
      </div>
    </aside>
    </>
  );
};

export default SideNav;
